import type { AgentFocus } from '@tunasong/agent-context'
import { startAgentTurn } from '@tunasong/redux'
import type { AgentStreamPayload, TunaThunkDispatch } from '@tunasong/redux'
import type { VirtualElement } from '@tunasong/ui-lib'
import { useSyncExternalStore } from 'react'

const mintId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export interface StartInlineTurnInput {
  agentId: string
  message: string
  focus?: AgentFocus
  trigger?: NonNullable<AgentStreamPayload['trigger']>
}

/**
 * Fire an interactive in-context agent turn (fire-and-forget). Marks the turn
 * `interactive` so the server offers the non-persisting inline propose tools
 * and the client renders the result inline. Returns the ids the inline
 * surfaces subscribe on.
 */
export const startInlineAgentTurn = (
  deps: { dispatch: TunaThunkDispatch },
  input: StartInlineTurnInput
): { agentId: string; requestId: string } => {
  const requestId = mintId()
  const params: AgentStreamPayload = {
    agentId: input.agentId,
    message: input.message,
    focus: input.focus,
    trigger: input.trigger ?? { type: 'chat' },
    interactive: true,
    requestId,
    turnId: mintId(),
  }
  startAgentTurn(deps.dispatch, params)
  return { agentId: input.agentId, requestId }
}

/** The turn the inline surfaces (ghost / answer note) currently render. */
export interface ActiveInlineTurn {
  agentId: string
  requestId: string
  /** Placement anchor for the surface — a live DOM node or a virtual (rect-only) element. */
  anchorEl: HTMLElement | VirtualElement | null
  /**
   * When true, the host renders ONLY an editable ghost — the caller (e.g. a
   * streaming palette command with its own response popup) already shows the
   * answer, so a transient answer note would double it.
   */
  suppressAnswerNote?: boolean
}

type Listener = () => void

const listeners = new Set<Listener>()
let active: ActiveInlineTurn | null = null

/**
 * Tiny module-scoped store (mirrors `scale-picker/picker-bus.ts`) tracking
 * which inline agent turn — if any — the interactive surfaces should render.
 *
 * `startInlineAgentTurn` deliberately does NOT call `setActiveInlineTurn`
 * itself: only the caller holds the block's `anchorEl` (a DOM node or Popper
 * virtual element resolved from the triggering selection/block), so wiring
 * the store is left to those callers.
 */
export const setActiveInlineTurn = (turn: ActiveInlineTurn): void => {
  active = turn
  for (const listener of listeners) {
    listener()
  }
}

/** Clear the active turn — called on resolve (ghost accept/reject) or dismiss (answer note). */
export const clearActiveInlineTurn = (): void => {
  active = null
  for (const listener of listeners) {
    listener()
  }
}

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): ActiveInlineTurn | null => active

/** Subscribe to the active inline turn. `null` when no inline turn is in flight. */
export const useActiveInlineTurn = (): ActiveInlineTurn | null =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
